import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Input, Empty, Spin, Typography, Button, Space, Tag, message, Tooltip, Select } from 'antd';
import { ApiOutlined, PlusOutlined, GlobalOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { searchPlugins } from '@/api/plugins';
import { listPluginCategories } from '@/api/pluginCategories';
import type { AiPlugin, PluginCategory } from '@/api/types';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { isGlobalTool, audiencesOf } from '@/features/tools/toolMeta';
import { CategoryChip } from '@/features/tools/CategoryChip';

const { Text } = Typography;

// Right-anchored drawer listing every tool in the catalog, so an admin can
// grant one to the current agent with a single click. Mirrors LinkKnowledgeDrawer.
//
// Already-granted tools are hidden. Global tools are shown but disabled with a
// note: they already reach every agent, so an explicit per-agent grant would be
// redundant — the honest thing is to say so rather than let someone "grant"
// something that changes nothing.

interface LinkToolsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Plugin NAMES already granted to the agent (hidden from the picker). */
  alreadyGrantedNames: Set<string>;
  agentDisplayName: string;
}

export const LinkToolsDrawer = observer(function LinkToolsDrawer({
  open,
  onClose,
  alreadyGrantedNames,
  agentDisplayName,
}: LinkToolsDrawerProps) {
  const { agentDetailStore: detail, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [allTools, setAllTools] = useState<AiPlugin[]>([]);
  const [categories, setCategories] = useState<PluginCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [grantingName, setGrantingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    searchPlugins({ pageSize: 200 })
      .then((res) => {
        if (cancelled) return;
        setAllTools(res.data ?? []);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load tools');
        setIsLoading(false);
      });
    listPluginCategories().then((c) => { if (!cancelled) setCategories(c); }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const categoryById = new Map(categories.map((c) => [c.id, c]));

  const candidates = allTools.filter((t) => {
    if (alreadyGrantedNames.has(t.name)) return false;
    if (categoryId !== 'all' && t.pluginCategoryId !== categoryId) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (t.name ?? '').toLowerCase().includes(q) ||
      (t.description ?? '').toLowerCase().includes(q)
    );
  });

  async function handleGrant(tool: AiPlugin) {
    setGrantingName(tool.name);
    try {
      await detail.grantPlugin(tool.name);
      message.success(`Granted ${tool.name} to ${agentDisplayName}`);
      trackEvent('AgentPlugin.Granted', { agentId: detail.agentId ?? -1, pluginName: tool.name });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to grant tool');
    } finally {
      setGrantingName(null);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="40%"
      destroyOnClose
      title={
        <Space size={10} align="center">
          <ApiOutlined style={{ color: colors.violet[600], fontSize: 18 }} />
          <span>Grant tools to this agent</span>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Pick a tool to let <b>{agentDisplayName}</b> call it. Each tool you grant is added to the
          list the agent sends to the model — grant only what it actually needs.
        </Text>

        <Input.Search
          placeholder="Search tools by name or description…"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select<number | 'all'>
          value={categoryId}
          onChange={setCategoryId}
          style={{ width: '100%' }}
          suffixIcon={<AppstoreOutlined />}
          options={[
            { label: 'All categories', value: 'all' },
            ...categories.map((c) => ({ label: c.name, value: c.id })),
          ]}
        />

        {error && (
          <div
            style={{
              padding: 12,
              background: isDark ? `${colors.red[700]}30` : colors.red[100],
              border: `1px solid ${colors.red[500]}`,
              borderRadius: 8,
              fontSize: 12.5,
              color: isDark ? colors.red[100] : colors.red[700],
            }}
          >
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isLoading ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search.trim()
                    ? 'No tools match your search.'
                    : alreadyGrantedNames.size > 0
                    ? 'Every tool in the catalog is already granted to this agent.'
                    : 'No tools exist in the catalog yet.'
                }
              />
            </div>
          ) : (
            candidates.map((tool) => (
              <ToolCandidateCard
                key={tool.id}
                tool={tool}
                category={tool.pluginCategoryId != null ? categoryById.get(tool.pluginCategoryId) : null}
                isDark={isDark}
                isGranting={grantingName === tool.name}
                onGrant={() => void handleGrant(tool)}
              />
            ))
          )}
        </div>
      </div>
    </Drawer>
  );
});

// ── Tool row card — name + description + reach + Grant button ──────────────

const ToolCandidateCard = function ToolCandidateCard({
  tool,
  category,
  isDark,
  isGranting,
  onGrant,
}: {
  tool: AiPlugin;
  category?: PluginCategory | null;
  isDark: boolean;
  isGranting: boolean;
  onGrant: () => void;
}) {
  const global = isGlobalTool(tool);

  return (
    <div
      style={{
        padding: '12px 14px',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : '#FFFFFF',
        borderRadius: 8,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: 4 }}>
          <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13.5, fontWeight: 600 }}>
            {tool.name}
          </Text>
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
            #{tool.id}
          </Text>
          {global && (
            <Tooltip title={`Already loaded into every ${audiencesOf(tool).join(' & ')} agent — granting it here changes nothing.`}>
              <Tag icon={<GlobalOutlined />} color="gold" style={{ fontSize: 10.5, marginLeft: 8 }}>
                Global
              </Tag>
            </Tooltip>
          )}
          <span style={{ marginLeft: 8 }}><CategoryChip category={category} /></span>
        </div>
        {tool.description && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {tool.description}
          </Text>
        )}
      </div>
      {global ? (
        <Tooltip title="This tool is global — it already reaches every agent, so there is nothing to grant.">
          <Button size="small" disabled>
            Already global
          </Button>
        </Tooltip>
      ) : (
        <Button
          type="primary"
          ghost
          size="small"
          icon={<PlusOutlined />}
          loading={isGranting}
          onClick={onGrant}
        >
          Grant
        </Button>
      )}
    </div>
  );
};
